import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiPlus, FiLink, FiDatabase, FiTrash2, FiActivity, FiEdit3, FiSave, FiX, FiCheck } = FiIcons;

const VirginPoolManagement = () => {
  const [pool, setPool] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);

  const fetchPool = async () => {
    const { data, error } = await supabase
      .from('app_virgin_pool')
      .select('*')
      .order('added_at', { ascending: false });
    
    if (error) console.error("Error pool:", error);
    setPool(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchPool(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newUrl.trim().startsWith('http')) {
      alert("Por favor, insira uma URL válida (começando com http/https)");
      return;
    }
    
    const { error } = await supabase
      .from('app_virgin_pool')
      .insert({ test_url: newUrl.trim() });

    if (!error) {
      setNewUrl('');
      fetchPool();
    } else {
      alert("Erro ao adicionar: URL pode já existir no estoque.");
    }
  };

  const handleUpdate = async (id) => {
    if (!editValue.trim().startsWith('http')) return;
    
    const { error } = await supabase
      .from('app_virgin_pool')
      .update({ test_url: editValue.trim() })
      .eq('id', id);

    if (!error) {
      setEditingId(null);
      fetchPool();
    } else {
      alert("Erro ao atualizar URL.");
    }
  };

  const removeUrl = async (id) => {
    if (!window.confirm("Deseja remover esta URL definitivamente?")) return;
    const { error } = await supabase.from('app_virgin_pool').delete().eq('id', id);
    if (!error) fetchPool();
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-slate-900 rounded-[2rem] p-8 text-white shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
            <SafeIcon icon={FiDatabase} className="text-8xl" />
          </div>
          <div className="relative z-10">
            <h3 className="font-black text-xs uppercase tracking-widest text-blue-400 mb-6 italic">Estoque Virgem</h3>
            <div className="text-6xl font-black mb-2 italic">{pool.filter(p => !p.is_assigned).length}</div>
            <p className="text-blue-200/50 text-[10px] font-black uppercase tracking-tight">URLs disponíveis para venda</p>
          </div>
        </div>

        <div className="md:col-span-2 bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm">
          <h3 className="font-black text-slate-900 mb-2 uppercase italic">Abastecer Sistema</h3>
          <p className="text-slate-400 text-xs font-bold mb-6">Adicione novas URLs de testes psicológicos para o estoque.</p>
          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <SafeIcon icon={FiLink} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" />
              <input 
                type="url" 
                value={newUrl} 
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://test.prof/unique-access-key" 
                className="w-full pl-14 pr-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 transition-all font-bold text-sm"
                required
              />
            </div>
            <button type="submit" className="bg-blue-600 text-white font-black px-10 py-5 rounded-2xl flex items-center justify-center hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 uppercase text-xs tracking-widest">
              <SafeIcon icon={FiPlus} className="mr-2" /> Adicionar
            </button>
          </form>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-sm">
        <div className="p-8 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-black text-slate-800 text-[10px] uppercase tracking-[0.3em] flex items-center italic">
            <SafeIcon icon={FiActivity} className="mr-3 text-blue-600" /> Gestão de URLs em Estoque
          </h3>
          <span className="text-[10px] font-black text-slate-400 uppercase">Total: {pool.length}</span>
        </div>
        
        <div className="max-h-[500px] overflow-y-auto divide-y divide-slate-50">
          {loading ? (
            <div className="p-20 text-center text-slate-300 font-black italic uppercase">Lendo estoque...</div>
          ) : pool.length === 0 ? (
            <div className="p-20 text-center text-slate-300 font-black italic uppercase">Estoque Vazio</div>
          ) : (
            pool.map(item => (
              <div key={item.id} className="p-6 flex items-center justify-between hover:bg-slate-50 transition-all group">
                <div className="flex-1 flex items-center space-x-4 min-w-0">
                  <div className={`w-3 h-3 rounded-full shrink-0 shadow-sm ${item.is_assigned ? 'bg-slate-200' : 'bg-green-500 animate-pulse'}`} />
                  
                  {editingId === item.id ? (
                    <div className="flex-1 flex items-center space-x-2">
                      <input 
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="flex-1 p-2 bg-white border border-blue-200 rounded-lg font-bold text-sm text-blue-600 outline-none"
                      />
                      <button onClick={() => handleUpdate(item.id)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        <SafeIcon icon={FiCheck} />
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300">
                        <SafeIcon icon={FiX} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col min-w-0">
                      <span className={`text-sm font-black italic truncate ${item.is_assigned ? 'text-slate-300 line-through' : 'text-slate-700'}`}>
                        {item.test_url}
                      </span>
                      <span className="text-[9px] font-black text-slate-400 uppercase mt-1">
                        Adicionado em {new Date(item.added_at).toLocaleString()}
                        {item.is_assigned && <span className="ml-2 text-red-400">• JÁ ATRIBUÍDO</span>}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  {!item.is_assigned && editingId !== item.id && (
                    <button 
                      onClick={() => { setEditingId(item.id); setEditValue(item.test_url); }}
                      className="p-3 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                      title="Editar URL"
                    >
                      <SafeIcon icon={FiEdit3} />
                    </button>
                  )}
                  <button 
                    onClick={() => removeUrl(item.id)} 
                    className="p-3 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                    title="Remover permanentemente"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default VirginPoolManagement;